import {
  attributeDefinitionIdFormat,
  chatGptListingIdFormat,
} from "@redotech/redo-ids/ids";
import {
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
  createSafeNullableEnumSchema,
  MeasurementSchema,
  ProductCategorySchema,
  WeightSchema,
} from "@redotech/redo-inventory-common/attributes";
import {
  ChatGptListingStatus,
  ChatGptProductFieldType,
} from "@redotech/redo-prisma-client/enums";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { BaseProductFamilyListingSchema, ListingType } from "../base-listing";
import { RedoListingStatus } from "../redo-listing-status";
import { ChatGptListingMediaSchema } from "./chatgpt-media";
import {
  ChatGptVariantListingSchema,
  ChatGptVariantMappingSchema,
} from "./chatgpt-variant";

export enum AgeGroup {
  Newborn = "newborn",
  Infant = "infant",
  Toddler = "toddler",
  Kids = "kids",
  Adult = "adult",
}
export const AgeGroupSchema = createEnum(AgeGroup);

export enum Condition {
  New = "new",
  Refurbished = "refurbished",
  Used = "used",
}
export const ConditionSchema = createEnum(Condition);

export const ChatGptListingStatusSchema = createEnum(ChatGptListingStatus);

export const ChatGptListingIdWithStatusSchema = z.object({
  id: chatGptListingIdFormat.schema,
  status: ChatGptListingStatusSchema,
});
export type ChatGptListingIdWithStatus = z.infer<
  typeof ChatGptListingIdWithStatusSchema
>;

export function convertChatGptListingStatusToRedoListingStatus(
  status: ChatGptListingStatus,
): RedoListingStatus {
  switch (status) {
    case ChatGptListingStatus.ACTIVE:
      return RedoListingStatus.ACTIVE;
    case ChatGptListingStatus.DRAFT:
      return RedoListingStatus.DRAFT;
    case ChatGptListingStatus.ARCHIVED:
      return RedoListingStatus.ARCHIVED;
    default:
      assertNever(status);
  }
}

export const ChatGptProductFieldTypeSchema = createEnum(
  ChatGptProductFieldType,
);

export const ChatGptListingSchema_ = BaseProductFamilyListingSchema.extend({
  listingType: z.literal(ListingType.ChatGpt),
  id: z.bigint(),
  gtin: z.string().nullable(),
  mpn: z.string().nullable(),
  title: z.string().nullable().default(""),
  description: z.string().nullable().default(""),
  link: z.string().nullable().default(""),
  productCategory: ProductCategorySchema.nullable(),
  brand: z.string().nullable().default(""),
  material: z.string().nullable().default(""),
  length: MeasurementSchema.nullable(),
  width: MeasurementSchema.nullable(),
  height: MeasurementSchema.nullable(),
  weight: WeightSchema.nullable(),
  ageGroup: AgeGroupSchema.nullable(),
  condition: ConditionSchema.nullable(),
  status: ChatGptListingStatusSchema,
  variants: z.array(ChatGptVariantListingSchema),
  media: z.array(ChatGptListingMediaSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ChatGptListingSchema: z.ZodType<ChatGptListing> =
  ChatGptListingSchema_;
export interface ChatGptListing extends z.infer<typeof ChatGptListingSchema_> {}

export const ChatGptProductAttributeType = {
  [ChatGptProductFieldType.title]: AttributeType.ShortText,
  [ChatGptProductFieldType.description]: AttributeType.LongText,
  [ChatGptProductFieldType.link]: AttributeType.ShortText,
  [ChatGptProductFieldType.product_category]: AttributeType.ProductCategory,
  [ChatGptProductFieldType.condition]: AttributeType.Enum,
  [ChatGptProductFieldType.brand]: AttributeType.ShortText,
  [ChatGptProductFieldType.material]: AttributeType.ShortText,
  [ChatGptProductFieldType.length]: AttributeType.Dimension,
  [ChatGptProductFieldType.width]: AttributeType.Dimension,
  [ChatGptProductFieldType.height]: AttributeType.Dimension,
  [ChatGptProductFieldType.weight]: AttributeType.Weight,
  [ChatGptProductFieldType.age_group]: AttributeType.Enum,
} satisfies Record<ChatGptProductFieldType, AttributeType>;

export const ChatGptProductAttributeSchema = {
  [ChatGptProductFieldType.title]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.title]
    ].nullable(),
  [ChatGptProductFieldType.description]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.description]
    ].nullable(),
  [ChatGptProductFieldType.link]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.link]
    ].nullable(),
  [ChatGptProductFieldType.product_category]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.product_category]
    ].nullable(),
  [ChatGptProductFieldType.condition]:
    createSafeNullableEnumSchema(ConditionSchema),
  [ChatGptProductFieldType.brand]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.brand]
    ].nullable(),
  [ChatGptProductFieldType.material]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.material]
    ].nullable(),
  [ChatGptProductFieldType.length]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.length]
    ].nullable(),
  [ChatGptProductFieldType.width]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.width]
    ].nullable(),
  [ChatGptProductFieldType.height]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.height]
    ].nullable(),
  [ChatGptProductFieldType.weight]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatGptProductFieldType.weight]
    ].nullable(),
  [ChatGptProductFieldType.age_group]:
    createSafeNullableEnumSchema(AgeGroupSchema),
};

export type ChatGptProductAttributeValueType = {
  [key in ChatGptProductFieldType]: (typeof ChatGptProductAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getChatGptProductAttributeValue<
  T extends ChatGptProductFieldType,
>(field: T, value: unknown): ChatGptProductAttributeValueType[T] | null {
  return ChatGptProductAttributeSchema[field].parse(
    value,
  ) as ChatGptProductAttributeValueType[T];
}

export function getChatGptProductAttributeType(field: ChatGptProductFieldType) {
  return ChatGptProductAttributeType[field];
}

const ChatGptProductMappingSchema_ = z.object({
  fieldName: ChatGptProductFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const ChatGptProductMappingSchema: z.ZodType<ChatGptProductMapping> =
  ChatGptProductMappingSchema_;
export interface ChatGptProductMapping
  extends z.infer<typeof ChatGptProductMappingSchema_> {}

const ChatGptMappingSchema_ = z.object({
  productMappings: z.array(ChatGptProductMappingSchema).readonly(),
  variantMappings: z.array(ChatGptVariantMappingSchema).readonly(),
});
export const ChatGptMappingSchema: z.ZodType<ChatGptMapping> =
  ChatGptMappingSchema_;
export interface ChatGptMapping extends z.infer<typeof ChatGptMappingSchema_> {}

export function getChatGptListingAttributeValue<
  T extends ChatGptProductFieldType,
>({
  listing,
  field,
}: {
  listing: ChatGptListing;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  switch (field) {
    case "title":
      fieldValue = listing.title;
      break;
    case "description":
      fieldValue = listing.description;
      break;
    case "material":
      fieldValue = listing.material;
      break;
    case "length":
      fieldValue = listing.length;
      break;
    case "width":
      fieldValue = listing.width;
      break;
    case "height":
      fieldValue = listing.height;
      break;
    case "weight":
      fieldValue = listing.weight;
      break;
    case "age_group":
      fieldValue = listing.ageGroup;
      break;
    case "product_category":
      fieldValue = listing.productCategory;
      break;
    case "condition":
      fieldValue = listing.condition;
      break;
    case "brand":
      fieldValue = listing.brand;
      break;
    case "link":
      fieldValue = listing.link;
      break;
    default:
      assertNever(field);
  }
  return ChatGptProductAttributeSchema[field].parse(fieldValue);
}
